import _classCallCheck from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/createClass";
import { defaults } from '../dom/utils/defaults.es.js';
import { isCustomEasing, isEasingList } from '../dom/utils/easing.es.js';
import { getEasingFunction } from './easing/utils/get-function.es.js';
import { slowInterpolateNumbers } from './utils/interpolate.es.js';

var NumberAnimation = /*#__PURE__*/function () {
  function NumberAnimation(output) {
    var _this = this;

    var keyframes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [0, 1];

    var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
        _ref$easing = _ref.easing,
        easing = _ref$easing === void 0 ? defaults.easing : _ref$easing,
        _ref$duration = _ref.duration,
        duration = _ref$duration === void 0 ? defaults.duration : _ref$duration,
        _ref$delay = _ref.delay,
        delay = _ref$delay === void 0 ? defaults.delay : _ref$delay,
        _ref$endDelay = _ref.endDelay,
        endDelay = _ref$endDelay === void 0 ? defaults.endDelay : _ref$endDelay,
        _ref$repeat = _ref.repeat,
        repeat = _ref$repeat === void 0 ? defaults.repeat : _ref$repeat,
        offset = _ref.offset,
        _ref$direction = _ref.direction,
        direction = _ref$direction === void 0 ? "normal" : _ref$direction;

    _classCallCheck(this, NumberAnimation);

    this.startTime = 0;
    this.rate = 1;
    this.t = 0;
    this.cancelTimestamp = 0;
    this.playState = "idle";
    this.finished = new Promise(function (resolve, reject) {
      _this.resolve = resolve;
      _this.reject = reject;
    });
    var totalDuration = duration * (repeat + 1);
    /**
     * We don't currently support custom easing (spring, glide etc) in NumberAnimation
     * (although this is completely possible), so this will have been hydrated by
     * animateStyle.
     */

    if (isCustomEasing(easing)) easing = "ease";
    var interpolate = slowInterpolateNumbers(keyframes, offset, isEasingList(easing) ? easing.map(getEasingFunction) : getEasingFunction(easing));

    this.tick = function (timestamp) {
      if (_this.pauseTime) timestamp = _this.pauseTime;
      var t = (timestamp - _this.startTime) * _this.rate;
      _this.t = t; // Convert to seconds

      t /= 1000; // Rebase on delay

      t = Math.max(t - delay, 0);
      /**
       * If this animation has finished, set the current time
       * to the total duration.
       */

      if (_this.playState === "finished") t = totalDuration;
      /**
       * Get the current progress (0-1) of the animation. If t is >
       * than duration we'll get values like 2.5 (midway through the
       * third iteration)
       */

      var progress = t / duration; // TODO progress += iterationStart

      /**
       * Get the current iteration (0 indexed). For instance the floor of
       * 2.5 is 2.
       */

      var currentIteration = Math.floor(progress);
      /**
       * Get the current progress of the iteration by taking the remainder
       * so 2.5 is 0.5 through iteration 2
       */

      var iterationProgress = progress % 1.0;

      if (!iterationProgress && progress >= 1) {
        iterationProgress = 1;
      }
      /**
       * If iteration progress is 1 we count that as the end
       * of the previous iteration.
       */


      iterationProgress === 1 && currentIteration--;
      /**
       * Reverse progress if we're not running in "normal" direction
       */

      var iterationIsOdd = currentIteration % 2;

      if (direction === "reverse" || direction === "alternate" && iterationIsOdd || direction === "alternate-reverse" && !iterationIsOdd) {
        iterationProgress = 1 - iterationProgress;
      }

      var latest = interpolate(t >= totalDuration ? 1 : Math.min(iterationProgress, 1));
      output(latest);
      var isAnimationFinished = _this.playState === "finished" || t >= totalDuration + endDelay;

      if (isAnimationFinished) {
        _this.playState = "finished";

        _this.resolve(latest);
      } else if (_this.playState !== "idle") {
        requestAnimationFrame(_this.tick);
      }
    };

    this.play();
  }

  _createClass(NumberAnimation, [{
    key: "play",
    value: function play() {
      var now = performance.now();
      this.playState = "running";

      if (this.pauseTime) {
        this.startTime = now - (this.pauseTime - this.startTime);
      } else if (!this.startTime) {
        this.startTime = now;
      }

      this.pauseTime = undefined;
      requestAnimationFrame(this.tick);
    }
  }, {
    key: "pause",
    value: function pause() {
      this.playState = "paused";
      this.pauseTime = performance.now();
    }
  }, {
    key: "finish",
    value: function finish() {
      this.playState = "finished";
      this.tick(0);
    }
  }, {
    key: "cancel",
    value: function cancel() {
      this.playState = "idle";
      this.tick(this.cancelTimestamp);
      this.reject(false);
    }
  }, {
    key: "reverse",
    value: function reverse() {
      this.rate *= -1;
    }
  }, {
    key: "commitStyles",
    value: function commitStyles() {
      this.cancelTimestamp = performance.now();
    }
  }, {
    key: "currentTime",
    get: function get() {
      return this.t;
    },
    set: function set(t) {
      if (this.pauseTime || this.rate === 0) {
        this.pauseTime = t;
      } else {
        this.startTime = performance.now() - t / this.rate;
      }
    }
  }, {
    key: "playbackRate",
    get: function get() {
      return this.rate;
    },
    set: function set(rate) {
      this.rate = rate;
    }
  }]);

  return NumberAnimation;
}();

export { NumberAnimation };